import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { falconClient } from '@/data/@clients';
import { clientKeys } from '@/data/@clients/client-keys';
import type {
  AcademicSituation,
  AcademicSituationDetails,
  AcademicStatusType,
  CreateAcademicSituationPayload,
} from '@/models/learning/academic-situation';

const baseUrl = '/api/learning/academic_situations';

interface AcademicSituationsResponse {
  academic_situations: AcademicSituation[];
}

export const useAcademicSituationsIndex = () => {
  const response = useQuery<AcademicSituationsResponse>({
    queryKey: [clientKeys.useAcademicSituationsIndex],
    queryFn: async () => await falconClient.get(baseUrl),
  });

  return {
    academicSituations: response.data?.academic_situations ?? [],
    ...response,
  };
};

export const useAcademicSituationShow = (situationId: string) => {
  const response = useQuery<AcademicSituationDetails>({
    queryKey: [clientKeys.useAcademicSituationShow, situationId],
    queryFn: async () => await falconClient.get(`${baseUrl}/${situationId}`),
    enabled: Boolean(situationId),
  });

  return {
    academicSituation: response.data,
    ...response,
  };
};

export const useCreateAcademicSituation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAcademicSituationPayload) =>
      falconClient.post<AcademicSituation>(baseUrl, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationsIndex],
        refetchType: 'all',
      });
    },
  });
};

export const useDeleteAcademicSituation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (situationId: string) =>
      falconClient.delete(`${baseUrl}/${situationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationsIndex],
        refetchType: 'all',
      });
    },
  });
};

export const useChangeAcademicStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      situationId,
      statusType,
    }: {
      situationId: string;
      statusType: AcademicStatusType;
    }) =>
      falconClient.post(`${baseUrl}/${situationId}/statuses`, {
        status_type: statusType,
        occurred_at: new Date().toISOString(),
      }),
    onSuccess: (_data, { situationId }) => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationsIndex],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationShow, situationId],
        refetchType: 'all',
      });
    },
  });
};

interface CreateAcademicStatusPayload {
  situationId: string;
  statusType: AcademicStatusType;
  occurredAt: string;
  description?: string;
  file?: File | null;
}

export const useCreateAcademicStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      situationId,
      statusType,
      occurredAt,
      description,
      file,
    }: CreateAcademicStatusPayload) => {
      const formData = new FormData();
      formData.append('status_type', statusType);
      formData.append('occurred_at', occurredAt);

      if (description) formData.append('description', description);
      if (file) formData.append('documents[]', file);

      return falconClient.post(`${baseUrl}/${situationId}/statuses`, formData);
    },
    onSuccess: (_data, { situationId }) => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationsIndex],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationShow, situationId],
        refetchType: 'all',
      });
    },
  });
};

export const useDeleteAcademicStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      situationId,
      statusId,
    }: {
      situationId: string;
      statusId: number;
    }) => falconClient.delete(`${baseUrl}/${situationId}/statuses/${statusId}`),
    onSuccess: (_data, { situationId }) => {
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationsIndex],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: [clientKeys.useAcademicSituationShow, situationId],
        refetchType: 'all',
      });
    },
  });
};
